'use client';
/**
 * @file     SupportForm.tsx
 * @purpose  Relta Agent support contact form. Posts via secureFetch to the Resend-backed route.
 */
import { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useAppTheme } from '@/core/theme/useAppTheme';
import { secureFetch } from '@/services/anti-scraping/secureFetch';
import { AntiSpamHoneypot } from '@/components/forms/AntiSpamHoneypot';

type Status = 'idle' | 'submitting' | 'success' | 'error';

export const SupportForm = () => {
  const { theme } = useAppTheme();
  const { t } = useTranslation();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [company, setCompany] = useState(''); // honeypot
  const [status, setStatus] = useState<Status>('idle');

  const disabled = status === 'submitting';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (disabled) return;
    setStatus('submitting');
    try {
      const res = await secureFetch('/api/relta-agent-support', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, subject, message, company }),
      });
      if (!res.ok) throw new Error('request_failed');
      setStatus('success');
      setName('');
      setEmail('');
      setSubject('');
      setMessage('');
    } catch {
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <Box
        sx={{
          p: 4,
          borderRadius: 1,
          backgroundColor: theme.semanticColors.surfaceCard,
          border: `1px solid ${theme.semanticColors.borderSubtle}`,
          textAlign: 'center',
        }}
      >
        <Typography
          sx={{
            fontSize: theme.typography.h4,
            fontWeight: 700,
            color: theme.semanticColors.textPrimary,
            mb: 1,
          }}
        >
          {t('pages.reltaAgentSupport.form.successTitle', { defaultValue: 'Message sent.' })}
        </Typography>
        <Typography
          sx={{ fontSize: theme.typography.bodyMd, color: theme.semanticColors.textSecondary }}
        >
          {t('pages.reltaAgentSupport.form.successBody', {
            defaultValue: "Thanks for reaching out. A reply will come by email.",
          })}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        p: { xs: 4, md: 6 },
        borderRadius: 1,
        backgroundColor: theme.semanticColors.surfaceCard,
        border: `1px solid ${theme.semanticColors.borderSubtle}`,
      }}
    >
      <Stack spacing={2.5}>
        <TextField
          name="name"
          autoComplete="name"
          label={t('pages.reltaAgentSupport.form.name', { defaultValue: 'Your name' })}
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          fullWidth
          disabled={disabled}
        />
        <TextField
          name="email"
          autoComplete="email"
          label={t('pages.reltaAgentSupport.form.email', { defaultValue: 'Your email' })}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          fullWidth
          disabled={disabled}
        />
        <TextField
          label={t('pages.reltaAgentSupport.form.subject', { defaultValue: 'Subject (optional)' })}
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          fullWidth
          disabled={disabled}
        />
        <TextField
          label={t('pages.reltaAgentSupport.form.message', { defaultValue: 'What do you need help with?' })}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          required
          fullWidth
          multiline
          minRows={5}
          disabled={disabled}
        />

        <AntiSpamHoneypot value={company} onValueChange={setCompany} />

        {status === 'error' && (
          <Typography sx={{ fontSize: theme.typography.bodySm, color: theme.semanticColors.statusError }}>
            {t('pages.reltaAgentSupport.form.error', {
              defaultValue: 'Something went wrong. Please try again, or email support@relta.ca directly.',
            })}
          </Typography>
        )}

        <Button
          type="submit"
          variant="contained"
          size="large"
          disabled={disabled}
          sx={{ textTransform: 'none', fontWeight: 600, alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
        >
          {disabled
            ? t('pages.reltaAgentSupport.form.submitting', { defaultValue: 'Sending…' })
            : t('pages.reltaAgentSupport.form.submit', { defaultValue: 'Send message' })}
        </Button>
      </Stack>
    </Box>
  );
};
